
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  category: string;
  image: string;
  description: string;
  rating: number;
  reviews: number;
  pricing: string;
}

interface ProductHeroProps {
  product: Product;
}

/**
 * ProductHero component renders a detailed hero section for a product.
 * Includes basic error‑handling for missing image URLs and rating values.
 */
export default function ProductHero({ product }: ProductHeroProps) {
  // Guard against missing image source to avoid broken <img> tag
  const imageSrc = product.image ?? '';

  // Ensure rating is a number within expected bounds
  const rating = typeof product.rating === 'number' && product.rating >= 0 ? product.rating : 0;
  const reviews = typeof product.reviews === 'number' && product.reviews >= 0 ? product.reviews : 0;

  // Helper to generate star icons based on rating
  const renderStars = () => {
    const fullStars = Math.floor(rating);
    return [...Array(5)].map((_, i) => (
      <i
        key={i}
        className={`ri-star-fill text-base ${i < fullStars ? 'text-amber-300' : 'text-white/30'}`}
        aria-hidden="true"
      ></i>
    ));
  };

  return (
    <section className="pt-28 pb-0 bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="pt-8 pb-16">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            Back to Products
          </Link>

          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 w-full">
              <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold rounded-full uppercase tracking-wider mb-5">
                {product.category}
              </span>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
                {product.name}
              </h1>
              <p className="text-lg text-white/85 mb-6 leading-relaxed max-w-xl">
                {product.description}
              </p>

              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center gap-1">{renderStars()}</div>
                <span className="text-white font-bold text-lg">{rating}</span>
                <span className="text-white/60 text-sm">
                  ({reviews.toLocaleString()} reviews)
                </span>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-teal-700 font-bold rounded-lg hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap shadow-lg shadow-black/10"
                >
                  Visit Website
                  <i className="ri-external-link-line" aria-hidden="true"></i>
                </a>
                <span className="text-2xl font-extrabold text-white">{product.pricing}</span>
              </div>
            </div>

            <div className="w-full md:w-[480px] flex-shrink-0">
              <div className="w-full h-[320px] rounded-xl overflow-hidden shadow-2xl shadow-black/20 border border-white/10">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={product.name}
                    title={`${product.name} - ${product.category}`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                    No image available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
